import React, { useEffect } from 'react';
import { useLang } from './LangContext';

function CookieDeclarationLoader() {
  useEffect(() => {
    const script = document.createElement('script');
    script.id = 'CookieDeclaration';
    script.src = 'https://consent.cookiebot.com/4858d2af-00b3-4242-8676-45606fb4dd88/cd.js';
    script.type = 'text/javascript';
    script.async = true;

    const container = document.getElementById('cookie-declaration-container');
    if (container && !document.getElementById('CookieDeclaration')) {
      container.appendChild(script);
    }

    return () => {
      const existingScript = document.getElementById('CookieDeclaration');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  return null;
}

function CookieButton({ children }) {
  return (
    <button
      onClick={() => {
        if (window.Cookiebot) window.Cookiebot.renew();
      }}
      className="info-button"
    >
      {children}
    </button>
  );
}

function PrivacyLayout({ title, updatedAt, intro, sections, contactHeading, contactBody, cookieHeading, cookieButtonLabel }) {
  return (
    <div className="info-page-shell">
      <CookieDeclarationLoader />
      <div className="info-page">
        <section className="content-card info-card info-hero-card">
          <p className="eyebrow">Pomodoro Flow</p>
          <h1>{title}</h1>
          <p className="info-meta">{updatedAt}</p>
          <p className="lead info-intro">{intro}</p>
        </section>

        <div className="info-page-stack">
          {sections.map((section) => (
            <section key={section.title} className="content-card info-card">
              <h2>{section.title}</h2>
              {section.paragraphs?.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
              {section.list?.length ? (
                <ul className="info-list">
                  {section.list.map((item) => (
                    <li key={item.label || item}>{item.label ? <><strong>{item.label}:</strong> {item.value}</> : item}</li>
                  ))}
                </ul>
              ) : null}
            </section>
          ))}

          <section className="content-card info-card">
            <h2>{contactHeading}</h2>
            <p>{contactBody}</p>
            <p>
              <a href="mailto:pomodoro.flow.yt@gmail.com" className="info-link">
                pomodoro.flow.yt@gmail.com
              </a>
            </p>
          </section>

          <section className="content-card info-card">
            <h2>{cookieHeading}</h2>
            <div className="info-actions">
              <CookieButton>{cookieButtonLabel}</CookieButton>
            </div>
            <div id="cookie-declaration-container"></div>
          </section>
        </div>
      </div>
    </div>
  );
}

const PrivacyDE = () => (
  <PrivacyLayout
    title="Datenschutzerklärung"
    updatedAt="Zuletzt aktualisiert: März 2026"
    intro="Diese Übersicht fasst zusammen, welche Daten bei Pomodoro Flow verarbeitet werden, auf welcher Grundlage das geschieht und welche Rechte du dabei hast."
    sections={[
      {
        title: '1. Verantwortlicher',
        paragraphs: ['Der Verantwortliche für die Datenverarbeitung auf dieser Website ist:'],
        list: [
          { label: 'Name', value: 'Tobias Mai' },
          { label: 'Adresse', value: 'Lindenstockstraße 31A, 51647 Gummersbach, Deutschland' },
          { label: 'E-Mail', value: 'pomodoro.flow.yt@gmail.com' },
        ],
      },
      {
        title: '2. Erhobene Daten',
        paragraphs: ['Wir erheben folgende Arten von Daten:'],
        list: [
          { label: 'Technische Daten', value: 'IP-Adresse, Browsertyp, Betriebssystem und Zugriffszeitpunkte – erhoben durch unseren Hosting-Anbieter.' },
          { label: 'Lokale Speicherdaten', value: 'Timer-Einstellungen wie Pomodoro-Dauer, Pausenzeiten und Intervalle werden lokal im Browser gespeichert und verlassen das Gerät nicht.' },
          { label: 'Cookies und Tracking-Daten', value: 'Nur mit deiner ausdrücklichen Einwilligung für Analyse und Werbung gemäß § 25 TTDSG.' },
        ],
      },
      {
        title: '3. Rechtsgrundlage (Art. 6 DSGVO)',
        paragraphs: ['Wir verarbeiten deine Daten auf Basis folgender Rechtsgrundlagen:'],
        list: [
          { label: 'Einwilligung (Art. 6 Abs. 1 lit. a DSGVO, § 25 TTDSG)', value: 'Für Marketing-Cookies, Google Ads und Google Tag Manager. Die Einwilligung kann jederzeit über die Cookie-Einstellungen widerrufen werden.' },
          { label: 'Berechtigtes Interesse (Art. 6 Abs. 1 lit. f DSGVO)', value: 'Für wesentliche Website-Funktionalität und Sicherheit.' },
          { label: 'Vertragserfüllung (Art. 6 Abs. 1 lit. b DSGVO)', value: 'Für die Bereitstellung des Timer-Services.' },
        ],
      },
      {
        title: '4. Werbung und Google Consent Mode v2',
        paragraphs: [
          'Diese Website verwendet Google AdSense in Verbindung mit Google Consent Mode v2. Dieser Standard teilt Google deinen Einwilligungsstatus mit, bevor irgendwelche Werbescripts ausgeführt werden.',
          'Ohne Marketing-Einwilligung: Google liefert nicht-personalisierte Werbung (NPA) – ohne Werbe-Cookies, ohne Tracking und ohne Weitergabe personenbezogener Daten.',
          'Mit Marketing-Einwilligung: Google kann personalisierte Werbung schalten und Werbe-Cookies setzen.',
          'Drittanbieter-Services, die wir mit deiner Einwilligung nutzen:',
        ],
        list: [
          { label: 'Google AdSense / Ads (Google LLC, USA)', value: 'Für Werbeanzeigen. Datenübermittlung in die USA auf Basis von Standardvertragsklauseln (SCCs) gemäß Art. 46 DSGVO.' },
          { label: 'Google Tag Manager (Google LLC, USA)', value: 'Zur Verwaltung von Tracking-Scripts.' },
          { label: 'Cookiebot (Cybot A/S, Dänemark)', value: 'Für die Verwaltung der Cookie-Einwilligung. Datenschutzrichtlinie: cookiebot.com/en/privacy-policy/' },
        ],
      },
      {
        title: '5. Datenspeicherung',
        list: [
          { label: 'Server-Logs', value: 'Gelöscht nach 7 Tagen.' },
          { label: 'Lokale Speicherung', value: 'Bis zum Löschen der Browser-Daten durch den Nutzer.' },
          { label: 'Cookies', value: 'Spezifische Aufbewahrungsfristen sind in der Cookie-Erklärung weiter unten aufgeführt.' },
        ],
      },
      {
        title: '6. Deine Rechte (Art. 15–22 DSGVO)',
        paragraphs: ['Du hast folgende Rechte bezüglich deiner personenbezogenen Daten:'],
        list: [
          'Auskunftsrecht (Art. 15 DSGVO)',
          'Recht auf Berichtigung (Art. 16 DSGVO)',
          'Recht auf Löschung (Art. 17 DSGVO)',
          'Recht auf Einschränkung der Verarbeitung (Art. 18 DSGVO)',
          'Recht auf Datenübertragbarkeit (Art. 20 DSGVO)',
          'Widerspruchsrecht (Art. 21 DSGVO)',
          'Recht, Einwilligung jederzeit zu widerrufen (Art. 7 Abs. 3 DSGVO)',
        ],
      },
      {
        title: '7. Beschwerderecht',
        paragraphs: [
          'Du hast das Recht, dich bei einer Datenschutzbehörde zu beschweren. Zuständige Behörde:',
          'Landesbeauftragte für Datenschutz und Informationsfreiheit Nordrhein-Westfalen, Kavalleriestraße 2–4, 40213 Düsseldorf – www.ldi.nrw.de',
        ],
      },
    ]}
    contactHeading="8. Kontakt"
    contactBody="Bei Fragen zum Datenschutz kontaktiere uns unter:"
    cookieHeading="9. Cookie-Einstellungen"
    cookieButtonLabel="Cookie-Einstellungen verwalten"
  />
);

const PrivacyEN = () => (
  <PrivacyLayout
    title="Privacy Policy"
    updatedAt="Last updated: March 2026"
    intro="This overview explains what data Pomodoro Flow processes, why that happens, and which rights you have regarding that processing."
    sections={[
      {
        title: '1. Controller',
        paragraphs: ['The controller responsible for data processing on this website is:'],
        list: [
          { label: 'Name', value: 'Tobias Mai' },
          { label: 'Address', value: 'Lindenstockstraße 31A, 51647 Gummersbach, Germany' },
          { label: 'Email', value: 'pomodoro.flow.yt@gmail.com' },
        ],
      },
      {
        title: '2. Data we collect',
        paragraphs: ['We collect the following types of data:'],
        list: [
          { label: 'Technical data', value: 'IP address, browser type, operating system, and access times — collected by our hosting provider.' },
          { label: 'Local storage data', value: 'Your timer settings (durations, intervals) are stored locally in your browser and never leave your device.' },
          { label: 'Cookies and tracking data', value: 'Only with your explicit consent for analytics and advertising, as required by GDPR and TTDSG.' },
        ],
      },
      {
        title: '3. Legal basis for processing (Art. 6 GDPR)',
        paragraphs: ['We process your data based on the following legal grounds:'],
        list: [
          { label: 'Consent (Art. 6(1)(a) GDPR, § 25 TTDSG)', value: 'For marketing cookies, Google Ads, and Google Tag Manager. Consent can be withdrawn at any time via cookie settings.' },
          { label: 'Legitimate interest (Art. 6(1)(f) GDPR)', value: 'For essential website functionality and security.' },
          { label: 'Contract performance (Art. 6(1)(b) GDPR)', value: 'For providing the timer service.' },
        ],
      },
      {
        title: '4. Advertising and Google Consent Mode v2',
        paragraphs: [
          'This website uses Google AdSense together with Google Consent Mode v2. This standard communicates your consent status to Google before any advertising scripts execute.',
          'Without marketing consent: Google serves non-personalised ads (NPA) — no advertising cookies are set, no tracking occurs, and no personal data is shared with Google for advertising.',
          'With marketing consent: Google may serve personalised ads and set advertising cookies.',
          'Third-party services used with your consent:',
        ],
        list: [
          { label: 'Google AdSense / Ads (Google LLC, USA)', value: 'For displaying advertisements. Data transfers to the USA are based on EU Standard Contractual Clauses (SCCs) per Art. 46 GDPR.' },
          { label: 'Google Tag Manager (Google LLC, USA)', value: 'For managing tracking scripts.' },
          { label: 'Cookiebot (Cybot A/S, Denmark)', value: 'For cookie consent management. Privacy policy: cookiebot.com/en/privacy-policy/' },
        ],
      },
      {
        title: '5. Data retention',
        list: [
          { label: 'Server logs', value: 'Deleted after 7 days.' },
          { label: 'Local storage', value: 'Until you clear your browser data.' },
          { label: 'Cookies', value: 'Specific retention periods are listed in the cookie declaration below.' },
        ],
      },
      {
        title: '6. Your rights (Art. 15–22 GDPR)',
        paragraphs: ['You have the following rights regarding your personal data:'],
        list: [
          'Right of access (Art. 15 GDPR)',
          'Right to rectification (Art. 16 GDPR)',
          'Right to erasure (Art. 17 GDPR)',
          'Right to restriction of processing (Art. 18 GDPR)',
          'Right to data portability (Art. 20 GDPR)',
          'Right to object (Art. 21 GDPR)',
          'Right to withdraw consent at any time (Art. 7(3) GDPR)',
        ],
      },
      {
        title: '7. Right to lodge a complaint',
        paragraphs: [
          'You have the right to lodge a complaint with a supervisory authority. The competent authority in Germany is:',
          'Landesbeauftragte für Datenschutz und Informationsfreiheit Nordrhein-Westfalen, Kavalleriestraße 2–4, 40213 Düsseldorf — www.ldi.nrw.de',
        ],
      },
    ]}
    contactHeading="8. Contact"
    contactBody="For any questions regarding this privacy policy or data protection, please contact:"
    cookieHeading="9. Cookie settings"
    cookieButtonLabel="Manage cookie settings"
  />
);

const Privacy = () => {
  const { lang } = useLang();
  return lang === 'de' ? <PrivacyDE /> : <PrivacyEN />;
};

export default Privacy;
